<template>
  <Loader :show="loading" v-if="loading"></Loader>
  <div v-else-if="newsFeeds.length">
    <MDBRow>
      <MDBCol col="12" class="mb-3">
        <NewsFeed
          v-for="(newsFeed, index) in newsFeeds"
          :key="newsFeed.id ?? `news-feed-${index}`"
          :newsFeed="newsFeed"
          :index="index"
        />
      </MDBCol>
    </MDBRow>
    <MDBRow v-if="paginationLinks.length > 0">
      <MDBCol cols="12" class="d-flex justify-content-center">
        <MDBPagination>
          <MDBPageItem
            v-for="(paginationLink, index) in localizedPaginationLinks"
            :key="`news-feed-page-${index}`"
          >
            <span
              class="cursor--pointer"
              :class="{ 'text-decoration-underline': paginationLink.active }"
              v-html="paginationLink.label"
              @click="paginate(paginationLink.url)"
            ></span>
          </MDBPageItem>
        </MDBPagination>
      </MDBCol>
    </MDBRow>
  </div>
</template>

<script setup>
import { MDBRow, MDBCol, MDBPagination, MDBPageItem } from "mdb-vue-ui-kit";

import NewsFeed from "@/components/NewsFeeds/Wrapper.vue";
import Loader from "@/components/General/Loader/index";
import { onMounted, computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const getPageFromQuery = () => {
  const pageParam = Number(route.query.page);
  return pageParam > 0 ? pageParam : 1;
};

const page = ref(getPageFromQuery());

const updatePageQuery = async (pageNumber) => {
  const query = { ...route.query };

  if (pageNumber > 1) {
    query.page = String(pageNumber);
  } else {
    delete query.page;
  }

  await router.replace({ query });
};

const fetchNewsFeeds = async () => {
  await store.dispatch("loadNewsFeeds", page.value);
};

const loadPage = async (pageNumber) => {
  page.value = pageNumber;
  store.dispatch("addCurrentNewsFeed", null);
  await updatePageQuery(pageNumber);
  await fetchNewsFeeds();
};

onMounted(async () => {
  page.value = getPageFromQuery();
  await fetchNewsFeeds();
});

watch(
  () => route.query.page,
  async () => {
    if (route.name !== "news_feeds") {
      return;
    }

    const pageNumber = getPageFromQuery();
    if (pageNumber === page.value) {
      return;
    }

    page.value = pageNumber;
    store.dispatch("addCurrentNewsFeed", null);
    await fetchNewsFeeds();
  },
);

const newsFeeds = computed(() => store.getters.getNewsFeeds);
const newsFeedsMeta = computed(() => store.getters.getNewsFeedsMeta);
const loading = computed(() => store.getters.getPending);

const paginationLinks = computed(() => newsFeedsMeta.value?.links || []);
const localizedPaginationLinks = computed(() =>
  paginationLinks.value.map((paginationLink) => ({
    ...paginationLink,
    label: getLocalizedPaginationLabel(paginationLink?.label),
  })),
);

const getLocalizedPaginationLabel = (label) => {
  if (typeof label !== "string") {
    return label;
  }

  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes("previous")) {
    return `&laquo; ${t("global.buttons.back")}`;
  }

  if (lowerLabel.includes("next")) {
    return `${t("global.buttons.next")} &raquo;`;
  }

  return label;
};

const paginate = async (url) => {
  if (!url) {
    return;
  }

  const nextPage = url.match(/\d+$/)?.[0];
  if (!nextPage) {
    return;
  }

  await loadPage(Number(nextPage));
};
</script>
